/**
 * @typedef {{ date: string; revenue?: number; mrr?: number; customers?: number; activeSubscriptions?: number }} RevenueSnapshot
 */

const TIMEFRAMES = [
  { key: "30d", label: "30D", spanDays: 30, points: 18 },
  { key: "3m", label: "3M", spanDays: 90, points: 22 },
  { key: "12m", label: "12M", spanDays: 365, points: 28 },
  { key: "all", label: "ALL", spanDays: 720, points: 34 },
];

export function getRevenueTimeframeOptions() {
  return TIMEFRAMES;
}

function shortLabel(iso) {
  const s = String(iso).slice(0, 10);
  const d = new Date(`${s}T12:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function parseIso(iso) {
  const s = String(iso).slice(0, 10);
  const d = new Date(`${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function stableUnit(seed, idx, slot = "v") {
  const s = `${seed || "revenue"}:${idx}:${slot}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

function variationFactor(seed, idx, t, growth) {
  const amp = Math.min(0.18, 0.045 + Math.abs(growth || 0) * 0.22);
  const waveA = Math.sin((t * Math.PI * 2.4) + stableUnit(seed, 1) * Math.PI);
  const waveB = Math.sin((t * Math.PI * 6.2) + stableUnit(seed, 2) * Math.PI);
  const jitter = (stableUnit(seed, idx, "j") - 0.5) * 0.055;
  return Math.max(0.62, 1 + waveA * amp + waveB * amp * 0.38 + jitter);
}

/**
 * Build chart-ready points from API history, snapshots, or verified peer monthly anchors.
 * @param {{
 *   revenueHistory?: RevenueSnapshot[];
 *   revenueSnapshots?: RevenueSnapshot[];
 *   anchors?: Record<string, unknown>;
 * }} startup
 * @param {string} timeframeKey
 * @returns {{ source: 'verified' | 'estimated'; points: { iso: string; label: string; revenue: number; mrr?: number }[] }}
 */
export function getRevenueSeries(startup, timeframeKey) {
  const cfg = TIMEFRAMES.find((t) => t.key === timeframeKey) || TIMEFRAMES[0];
  const hist = Array.isArray(startup?.revenueHistory) ? startup.revenueHistory : [];
  const snaps = Array.isArray(startup?.revenueSnapshots) ? startup.revenueSnapshots : [];
  const rawSeries = hist.length >= 2 ? hist : snaps.length >= 2 ? snaps : null;

  if (rawSeries) {
    const sorted = [...rawSeries]
      .map((p) => ({
        date: String(p.date || p.iso || "").slice(0, 10),
        revenue: Number(p.revenue) || 0,
        mrr: p.mrr != null ? Number(p.mrr) : undefined,
      }))
      .filter((p) => p.date.length >= 8)
      .sort((a, b) => a.date.localeCompare(b.date));

    if (sorted.length < 2) {
      return { source: "verified", points: [] };
    }

    const lastD = parseIso(sorted[sorted.length - 1].date);
    if (!lastD) {
      return { source: "verified", points: [] };
    }

    let slice = sorted;
    if (cfg.key !== "all") {
      const start = new Date(lastD);
      start.setDate(start.getDate() - cfg.spanDays);
      const filtered = sorted.filter((p) => {
        const d = parseIso(p.date);
        return d && d >= start;
      });
      if (filtered.length >= 2) slice = filtered;
    }

    const points = slice.map((p) => ({
      iso: p.date,
      label: shortLabel(p.date),
      revenue: p.revenue,
      mrr: p.mrr,
    }));
    return { source: "verified", points };
  }

  const anchors = startup?.anchors || {};
  const last30 = Number(anchors.last30_days_usd);
  const mrr = Number(anchors.mrr_usd);
  const total = Number(anchors.total_revenue_usd);
  const gPct = Number(anchors.growth30d_pct);
  const gmPct = Number(anchors.growth_mrr_30d_pct);
  const seed = String(anchors.slug || anchors.last30_days_usd || anchors.mrr_usd || "revenue");
  const g = Number.isFinite(gPct) ? gPct / 100 : 0;
  const gm = Number.isFinite(gmPct) ? gmPct / 100 : 0;

  if (!Number.isFinite(last30) && !Number.isFinite(mrr) && !Number.isFinite(total)) {
    return { source: "estimated", points: [] };
  }

  const baseMonthly = Number.isFinite(last30) && last30 > 0 ? last30 : Number.isFinite(mrr) && mrr > 0 ? mrr : 0;
  const mrrBase = Number.isFinite(mrr) && mrr > 0 ? mrr : baseMonthly * 0.85;
  const now = new Date();
  const { spanDays, points } = cfg;
  const out = [];

  for (let i = 0; i < points; i += 1) {
    const t = points === 1 ? 1 : i / (points - 1);
    const growthLift = Math.max(0.35, Math.exp(g * (t - 1) * 0.65));
    const mrrLift = Math.max(0.35, Math.exp(gm * (t - 1) * 0.45));
    const wave = variationFactor(seed, i, t, g || gm);
    let rev = 0;
    if (cfg.key === "all" && Number.isFinite(total) && total > 0) {
      const weight = Math.max(0.18, (0.3 + 0.95 * t) * wave);
      const normalizer = points * 0.82;
      rev = (total / normalizer) * weight;
    } else if (baseMonthly > 0) {
      const bucketDays = spanDays / points;
      const dailyEq = baseMonthly / 30;
      rev = dailyEq * bucketDays * growthLift * wave;
    } else if (Number.isFinite(total) && total > 0) {
      rev = (total / points) * (0.25 + 0.9 * t) * wave;
    }
    const mrrPt = mrrBase * mrrLift * wave;
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - Math.round((1 - t) * spanDays));
    const iso = d.toISOString().slice(0, 10);
    out.push({
      label: shortLabel(iso),
      iso,
      revenue: Math.max(0, Math.round(rev)),
      mrr: Math.max(0, Math.round(mrrPt)),
    });
  }
  return { source: "estimated", points: out };
}
